// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import { styled } from '@compiled/react';
import {
	type ProjectType,
	SOFTWARE_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { token } from '@atlaskit/tokens';

import HelpPanelButton from '@atlassian/jira-help-panel-button/src/index.tsx';
import { defineMessages } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type {
	MessageDescriptorV2 as MessageDescriptor,
	IntlShapeV2 as IntlShape,
} from '@atlassian/jira-intl/src/v2/types.tsx';
import type { AnalyticsData } from '../../state/analytics/types.tsx';
import imageUrl from './components.svg';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	paddingTop: token('space.300'),
	paddingRight: 0,
	paddingBottom: 0,
	paddingLeft: 0,
});

const messages = defineMessages({
	header: {
		id: 'project-components.empty-placeholder-nothing-added-yet',
		defaultMessage: "This project doesn't have any components, yet",
		// eslint-disable-next-line @atlassian/i18n/require-description
		description: '',
	},
	description: {
		id: 'project-components.empty-placeholder-components-are-subsections',

		defaultMessage:
			'Components are subsections of a project. Use them to group issues within a project into smaller parts.',

		// eslint-disable-next-line @atlassian/i18n/require-description
		description: '',
	},
	addComponent: {
		id: 'project-components.empty-placeholder-add-a-component',
		defaultMessage: 'Create component',
		// eslint-disable-next-line @atlassian/i18n/require-description
		description: '',
	},
	learnMore: {
		id: 'project-components.empty-placeholder-read-more',
		defaultMessage: 'Learn more',
		// eslint-disable-next-line @atlassian/i18n/require-description
		description: '',
	},
	learnMoreAriaLabelText: {
		id: 'project-components.empty-placeholder-read-more-aria-label-text',
		defaultMessage: 'Learn more about components',
		description:
			'With this aria-label text the screen reader users will understand the purpose of the link',
	},
	learnMoreJiraComponentsAriaLabelText: {
		id: 'project-components.empty-placeholder-read-more-jira-components-aria-label-text',
		defaultMessage: 'Learn how to configure Jira components',
		description:
			'With this aria-label text the screen reader users will understand the purpose of the link',
	},
});

type Props = {
	intl: IntlShape;
	canAddComponent: boolean;
	onAddComponent: () => void;
	analyticsData: AnalyticsData;
	projectType: ProjectType;
};

// eslint-disable-next-line jira/react/no-class-components
class EmptyPlaceholderView extends PureComponent<Props> {
	renderAddComponentButton() {
		return (
			<Button appearance="primary" onClick={this.props.onAddComponent}>
				{this.props.intl.formatMessage(messages.addComponent)}
			</Button>
		);
	}

	renderLinkButton() {
		let articleId: string;
		let fallbackLink: string;
		let learnMoreAriaLabelText: MessageDescriptor;
		if (this.props.projectType === SOFTWARE_PROJECT) {
			// For JSW projects, show the newer Jira Components help articles (which contains references to Compass Components)
			articleId = 'XFBVB5cRw96Y4KvDXxiuM';
			fallbackLink =
				'https://support.atlassian.com/jira-software-cloud/docs/configure-jira-components/';
			learnMoreAriaLabelText = messages.learnMoreJiraComponentsAriaLabelText;
		} else {
			// For non-JSW projects (JSM), show the original Jira Components help articles (Compass Components are not available in JSM)
			articleId = 'kG5jarMna4VfUEbBkegtI';
			fallbackLink = 'https://confluence.atlassian.com/display/ADMINJIRACLOUD/Managing+components';
			learnMoreAriaLabelText = messages.learnMoreAriaLabelText;
		}

		return (
			<HelpPanelButton
				aria-label={this.props.intl.formatMessage(learnMoreAriaLabelText)}
				articleId={articleId}
				appearance="subtle-link"
				spacing="none"
				fallbackComponent={
					<Button
						aria-label={this.props.intl.formatMessage(learnMoreAriaLabelText)}
						appearance="subtle-link"
						href={fallbackLink}
						target="_blank"
					>
						{this.props.intl.formatMessage(messages.learnMore)}
					</Button>
				}
			>
				{this.props.intl.formatMessage(messages.learnMore)}
			</HelpPanelButton>
		);
	}

	render() {
		const {
			canAddComponent,
			intl: { formatMessage },
		} = this.props;

		return (
			<Container>
				<EmptyState
					imageUrl={imageUrl}
					width="narrow"
					header={formatMessage(messages.header)}
					description={formatMessage(messages.description)}
					primaryAction={!canAddComponent ? null : this.renderAddComponentButton()}
					tertiaryAction={this.renderLinkButton()}
				/>
			</Container>
		);
	}
}

export default injectIntl(EmptyPlaceholderView);
